
import Header from './Header2';
import Popup from './Popup2';
import "../styles/Project.css";
import Vector from "../assets/svg/editIcon.svg";
import Delete from "../assets/svg/DeleteIcon.svg";
import filterOpenCloseIcon from "../assets/svg/filterOpenCloseIcon.svg";
import { useState, useRef, useEffect } from "react";
import DatePicker from "../Component/DatePicker";
import ReactPaginate from 'react-paginate';
import { useNavigate } from 'react-router-dom';
import { ToastContainer, toast } from "react-toastify"
import axios from 'axios';
import axiosInstance from '../axiosInstance';
import ConfirmDialog from '../ConfirmDialog/ConfirmDialog';
import CommonFooter from '../CommonFooter/CommonFooter';
import ProjectFilter from './ProjectFilter';
import CommonPagination from '../CommonPagination/CommonPagination';
import { useRolePermission } from '../hooks/useRolePermission';

const Project = () => {

  const checkPermission = useRolePermission();

  const token = "";
  // State management
  const [isPopupOpen, setIsPopupOpen] = useState(false);
  const [selectedRows, setSelectedRows] = useState([]);
  const popupRef = useRef(null);
  const [limit, setLimit] = useState(5);
  const [skip, setSkip] = useState(0);
  const [error, setError] = useState(null);
  const [tableData, setTableData] = useState([]);
  const [totalPages, setTotalPages] = useState(0);
  const [total, setTotal] = useState(0);
  const [currentPage, setCurrentPage] = useState(0);
  const [loading, setLoading] = useState(false);
  const [sortBy, setSortBy] = useState("dateCreated");
  const [projects, setProjects] = useState([]);
  const [showPopup, setShowPopup] = useState(false);
  const [selectedProject, setSelectedProject] = useState(null);
  const [isEditing, setIsEditing] = useState(false);
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [deleteId, setDeleteId] = useState(null);
  const [filteredData, setFilteredData] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [showFilter, setShowFilter] = useState(true);

  const [filters, setFilters] = useState({
    project: [],
    plantCode: [],
    fromDate: null,
    toDate: null,
    profitCenter: [],
    city: [],
    state: [],
    zone: [],
    division: ""
  });

  const handleApplyFilters = (filterData) => {
    setFilters(filterData);
    setSkip(0);
    setCurrentPage(0);
  };

  const handleResetFilters = () => {
    setFilters({
      project: [],
      plantCode: [],
      fromDate: null,
      toDate: null,
      profitCenter: [],
      city: [],
      state: [],
      zone: [],
      division: ""
    });
    setSkip(0);
    setCurrentPage(0);
  };

  // Derived state
  const isAllSelected = selectedRows.length === tableData.length && tableData.length > 0;
  const isAnySelected = selectedRows.length > 0;

  const navigate = useNavigate();

  const handleAddNewClick = () => {
    navigate("/projects_popup");
  };;

  const closePopup = () => {
    setShowPopup(false);
    setIsEditing(false);
    setSelectedProject(null);
  };
  const handleEditClick = async (project) => {
    try {
      setLoading(true);

      const response = await axiosInstance.get(
        `/costsolution/projects/get/${project.id}`
      );

      console.log("Full API response:", response);
      console.log("API response data:", response.data);

      if (response.data && response.data.projects) {
        navigate(`/projects_popup`, { state: { project: response.data.projects } });
      } else {
        throw new Error("Project data not found");
      }
    } catch (error) {
      setError(error.message);
      console.error("Error fetching project details:", error);
      toast.error("Failed to load project details: " + error.message);
    } finally {
      setLoading(false);
    }
  };

  const handleDateSelection = (date) => {
    console.log("Selected Date:", date);
  };

  const handleSearch = async (term) => {
    setSearchTerm(term);
    if (!term.trim()) {
      fetchProjects('');
      return;
    }

    try {
      setLoading(true);
      const response = await axiosInstance.post(
        `/costsolution/projects?limit=${limit}&skip=0&sortBy=${sortBy}&search=${term}`
      );

      const projects = response.data?.projects || [];
      setTableData(projects);
      setFilteredData(projects);

      const newTotal = response.data?.totalCount || 0;
      setTotal(newTotal);
      setTotalPages(Math.ceil(newTotal / limit));
      setCurrentPage(0);
      setSkip(0);
    } catch (err) {
      console.error(err);
      toast.error("Failed to fetch projects: " + err.message);
    } finally {
      setLoading(false);
    }
  };

  const fetchProjects = async (searchQuery = '') => {
    setLoading(true);
    setError(null);

    try {
      const payload = {
        filter: {
          project: filters.project || '',
          plant_code: filters.plantCode || '',
          from_date: filters.fromDate
            ? new Date(filters.fromDate).toISOString().split("T")[0]
            : '',
          to_date: filters.toDate
            ? new Date(filters.toDate).toISOString().split("T")[0]
            : '',
          profit_centre: filters.profitCenter || '',
          city: filters.city || '',
          state: filters.state || '',
          zones: filters.zone || '',
          division_type: filters.division || ''
        }
      };

      const response = await axiosInstance.post(
        `/costsolution/projects?limit=${limit}&skip=${skip}&sortBy=${sortBy}&search=${searchQuery}`,
        payload
      );

      if (response.data && Array.isArray(response.data.projects)) {
        setTableData(response.data.projects);
        setFilteredData(response.data.projects);
        const newTotal = response.data.totalCount || 0;
        setTotal(newTotal);
        setTotalPages(Math.ceil(newTotal / limit));
      } else {
        setTableData([]);
        setTotal(0);
        setTotalPages(0);
      }
    } catch (error) {
      setError(error.message);
      console.error("Fetch error:", error);
    } finally {
      setLoading(false);
    }
  };

  // Handle page changes
  const handlePageClick = ({ selected }) => {
    const newSkip = selected * limit;
    setCurrentPage(selected);
    setSkip(newSkip);
    setSelectedRows([]);
  };

  const handleLimitChange = (e) => {
    const newLimit = Number(e.target.value);
    setLimit(newLimit);
    setCurrentPage(0);
    setSkip(0);
  };

  // Handle sorting change
  const handleSortChange = (e) => {
    const value = e.target.value;
    setSortBy(value);
    setSkip(0);
    setCurrentPage(0);
  };

  // Handle row selection
  const handleSelectAll = () => {
    setSelectedRows(isAllSelected ? [] : tableData.map((row) => row.id));
  };

  const handleRowSelect = (id) => {
    setSelectedRows((prev) =>
      prev.includes(id) ? prev.filter((rowId) => rowId !== id) : [...prev, id]
    );
  };

  const handleDelete = (id) => {
    setDeleteId(id);
    setConfirmOpen(true);
  };

  const confirmDelete = async () => {
    if (!deleteId) return;

    try {
      setLoading(true);
      const response = await axiosInstance.delete(`/costsolution/projects/delete/${deleteId}`);

      if (response?.data?.success) {
        setTableData(prev => prev.filter(project => project.id !== deleteId));
        setSelectedRows(prev => prev.filter(rowId => rowId !== deleteId));
        setTotal(prev => prev - 1);
        fetchProjects();
        toast.success("Project deleted successfully!");
      } else {
        toast.error(response?.data?.message || "Deletion failed");
      }
    } catch (error) {
      console.error("Delete error:", error);
      toast.error(error?.response?.data?.message || "Failed to delete the project");
    } finally {
      setLoading(false);
      setConfirmOpen(false);
      setDeleteId(null);
    }
  };

  const cancelDelete = () => {
    setConfirmOpen(false);
    setDeleteId(null);
  };

  useEffect(() => {
    fetchProjects(searchTerm);
  }, [skip, limit, sortBy, filters]);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (popupRef.current && !popupRef.current.contains(event.target)) {
        closePopup();
      }
    };

    if (showPopup) {
      document.addEventListener("mousedown", handleClickOutside);
    }
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [showPopup]);

  const handleAddProject = async (newProject) => {
    try {
      setLoading(true);

      const response = await axiosInstance.post(
        `/costsolution/projects`, newProject
      );


      if (response.status === 201) {
        const createdProject = response.data;
        setTableData(prev => [createdProject, ...prev]);
        setTotal(prev => prev + 1);
        toast.success("Project created successfully!");
      } else {
        throw new Error(response.data.message || `Failed to create project`);
      }
    } catch (error) {
      setError(error.message);
      toast.error("Failed to create project: " + error.message);
    } finally {
      setLoading(false);
    }
  };

  const handleRowClick = async (project) => {
    if (!checkPermission("Project", "view_permission")) {
      return;
    }

    try {
      const response = await axiosInstance.get(
        `/costsolution/projects/get/${project.id}`
      );

      if (response.data && response.data.projects) {
        navigate(`/projectviewpage`, { state: { project: response.data.projects } });
      } else {
        throw new Error("Project data not found");
      }
    } catch (error) {
      setError(error.message);
      console.error("Error fetching project details:", error);
      toast.error("Failed to load project details: " + error.message);
    } finally {
      setLoading(false);
    }
  };



  return (
    <>
      <Header onSearch={handleSearch} />
      <div className="container">
        <div
          className="header"
          style={{ marginBottom: showFilter ? "20px" : "0px" }}
        >
          <div className="left-header">
            <h3>All Projects ({total})</h3>
          </div>
          <div className="right-header">
            {/* {!isAnySelected ? (
              <>
                <DatePicker onDateChange={handleDateSelection} />
                <h4>Sort by:</h4>
                <div className="dropdown">
                  <select 
                    id="options" 
                    className="dropdown-select"
                    value={sortBy}
                    onChange={handleSortChange}
                    disabled={loading}
                  >
                    <option value="dateCreated">Date Created</option>
                    <option value="name">Name</option>
                    <option value="code">Code</option>
                  </select>
                </div>
              </>
            ) : (
              <button 
                id="deleteBtn" 
                className="deleteBtn" 
                onClick={() => handleDelete(selectedRows[0])}
                disabled={loading}
              >
                Delete ({selectedRows.length})
              </button>
            )} */}
              <>
            {checkPermission("Project", "create_permission") && (
              <button id="addNewBtn" onClick={handleAddNewClick}>Add New</button>
            )}
                <div
                  className={`project-popup-filter-setshow-image ${showFilter ? "active" : ""}`}
                  onClick={() => setShowFilter(!showFilter)}
                >
                  <img
                    src={filterOpenCloseIcon}
                    alt="Filter"
                    className="project-popup-filter-icon"
                    title={showFilter ? "Hide Filters" : "Show Filters"}
                  />
                </div>
              </>
          </div>
        </div>

        <div className={`filteropenclose-wrapper ${showFilter ? "open" : ""}`}>
          <ProjectFilter
            onApply={handleApplyFilters}
            onReset={handleResetFilters}
          />
        </div>

        <div className="table-container" style={{ overflowX: 'auto', whiteSpace: 'nowrap', scrollbarWidth: 'none', OverflowStyle: 'none', WebkitOverflowScrolling: 'touch', scrollBehavior: 'smooth', marginTop: showFilter ? "8px" : "0px" }}>
          <table >
            <thead>
              <tr>
                {/* <th style={{ width: "36px", minWidth: "36px", textAlign: "center", padding: "0" }}>
                  <input
                    type="checkbox"
                    checked={isAllSelected}
                    onChange={handleSelectAll}
                    disabled={tableData.length === 0 || loading}
                    style={{ width: "14px", height: "14px", margin: 0 }}
                  />
                </th> */}
                <th className="table-heading" style={{ width: "50px", minWidth: "50px", textAlign: "center", padding: "4px" }} >
                  S.No
                </th>
                <th className='table-heading'>Code</th>
                <th className='table-heading'>Name</th>
                {/* <th className='table-heading'>Division Type</th>
                <th className='table-heading'>Plant Code</th> */}
                <th className='table-heading'>Created Date</th>
                <th ></th>
              </tr>
            </thead>
            <tbody>
              {filteredData.length > 0 ? (
                filteredData.map((project, index) => (
                  console.log("project", project),
                  <tr key={`${project.id}-${index}`}>
                    {/* <td className="table-cell" style={{ width: "36px", minWidth: "36px", textAlign: "center", padding: "0" }}>
                      <input
                        type="checkbox"
                        checked={selectedRows.includes(project.id)}
                        onChange={() => handleRowSelect(project.id)}
                        disabled={loading}
                        style={{ width: "14px", height: "14px", margin: 0 }}
                      />
                    </td> */}
                    <td
                      className="table-cell"
                      style={{ width: "50px", minWidth: "50px", textAlign: "center", padding: "4px" }}
                    >
                      {skip + index + 1}
                    </td>

                    <td className='table-cell' style={{ cursor: 'pointer' }} onClick={() => handleRowClick(project)}>{project.code || '-'}</td>
                    <td
                      className='table-cell'
                      style={{ cursor: 'pointer' }}
                      onClick={() => handleRowClick(project)}
                       title={project.name || ''}
                    >
                      {project.name
                        ? project.name.length > 50
                          ? project.name.substring(0, 50) + "..."
                          : project.name
                        : '-'}
                    </td>
                    {/* <td className='table-cell' style={{ cursor: 'pointer' }} onClick={() => handleRowClick(project)}>{project.division_type?.map(d => d.toUpperCase()).join(", ")}</td>
                    <td className='table-cell' style={{ cursor: 'pointer' }} onClick={() => handleRowClick(project)}>{project.plant_code}</td> */}
                    <td className='table-cell' style={{ cursor: 'pointer' }} onClick={() => handleRowClick(project)}>{project.createddate}</td>
                    <td>
                      <div className="actions">

                        {checkPermission("Project", "edit_permission") && (
                          <span
                            className="edit-icon"
                            onClick={() => handleEditClick(project)}
                            disabled={loading} style={{ cursor: 'pointer' }}
                          >
                            <img src={Vector} alt="Edit" width="20" height="20" />
                          </span>
                        )}

                        {checkPermission("Project", "delete_permission") && (
                          <span
                            className="delete-icon"
                            onClick={() => handleDelete(project.id)}
                            disabled={loading} style={{ cursor: 'pointer' }}
                          >
                            <img src={Delete} alt="Delete" width="40" height="40" />
                          </span>
                        )}
                      </div>
                    </td>

                  </tr>
                ))) : (
                <tr>
                  {filteredData.length === 0 && (
                    <td colSpan="7" style={{ textAlign: 'center', padding: '20px', fontSize: '14px', color: '#666' }}>
                      {searchTerm
                        ? `No Project found matching "${searchTerm}"`
                        : "No Project found for the selected filters"}
                    </td>
                  )}
                </tr>
              )}
            </tbody>
          </table>
        </div>

          <CommonPagination
            currentPage={currentPage}
            totalPages={totalPages}
            limit={limit}
            onPageChange={handlePageClick}
            onLimitChange={handleLimitChange}
          />

      </div>
      <CommonFooter />
      <ToastContainer position="top-right" autoClose={3000} hideProgressBar={false} />
      <ConfirmDialog
        open={confirmOpen}
        title="Delete Confirmation"
        message="Are you sure you want to delete this Project?"
        confirmText="Delete"
        cancelText="Cancel"
        onConfirm={confirmDelete}
        onCancel={cancelDelete}
      />
    </>
  );
};

export default Project;